// react
import React, { useMemo, useCallback } from 'react';
import styled from 'styled-components';

// components
import { Checkbox, BaseField, FieldProps } from '@/components/atoms';

/**
 * BooleanTable is an input component which allows users to modify boolean
 * values in a table format.
 *
 * Example value:
 * {
 *   "washing": {
 *     "morning": true,
 *     "afternoon": false,
 *     "evening": true,
 *   },
 *   "brushing-teeth": {
 *     "morning": false,
 *     "afternoon": false,
 *     "evening": true,
 *   },
 * }
 */
export const BooleanTable = ({
  className,
  columns,
  rows,
  title,
  id,
  label,
  description,
  tooltip,
  isRequired,
  isHideTitle,
  value,
  onChange,
}: BooleanTableProps) => {
  // memo
  const valueObject = useMemo(() => JSON.parse(value || '{}'), [value]);

  // methods
  const handleCheckboxChange = useCallback(
    (row: string, column: string, value: boolean) => {
      const newValue = { ...valueObject };
      if (!newValue[row]) {
        newValue[row] = {};
      }
      newValue[row][column] = value;
      onChange?.(JSON.stringify(newValue));
    },
    [valueObject, onChange],
  );

  return (
    <BaseField
      id={id}
      label={label}
      description={description}
      tooltip={tooltip}
      isRequired={isRequired}
      isHideTitle={isHideTitle}
    >
      <Table>
        {/* header */}
        <thead>
          <tr>
            <Title>{title}</Title>
            {columns.map((column) => (
              <ColumnHeader key={column}>{column}</ColumnHeader>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <Row key={row}>
              <RowTitle>{row}</RowTitle>
              {columns.map((column) => (
                <CheckboxWrapper key={`${row}-${column}`}>
                  <Checkbox
                    key={`${id}-${row}-${column}`}
                    id={`${id}-${row}-${column}`}
                    value={valueObject?.[row]?.[column]}
                    onChange={(value) =>
                      handleCheckboxChange(row, column, value)
                    }
                  />
                </CheckboxWrapper>
              ))}
            </Row>
          ))}
        </tbody>
      </Table>
    </BaseField>
  );
};

const Table = styled.table`
  border-collapse: collapse;
  border-spacing: 0;
  width: 100%;
`;

const Title = styled.th`
  padding-bottom: ${(props) => props.theme.spacing[12]};
  font: ${(props) => props.theme.typography.body.md.medium};
  color: ${(props) => props.theme.color.primary[80]};
  text-align: left;
`;

const ColumnHeader = styled.th`
  padding: 0 ${(props) => props.theme.spacing[12]};
  padding-bottom: ${(props) => props.theme.spacing[12]};
  font: ${(props) => props.theme.typography.body.md.medium};
  color: ${(props) => props.theme.color.neutral[100]};
  min-width: 100px;
  text-align: left;
`;

const RowTitle = styled.td`
  padding: ${(props) => props.theme.spacing[12]} 0;
  padding-right: ${(props) => props.theme.spacing[12]};
  border-top: 1px solid ${(props) => props.theme.color.neutral[40]};
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[90]};
  text-align: left;
`;

const CheckboxWrapper = styled.td`
  padding: ${(props) => props.theme.spacing[12]} 0;
  border-top: 1px solid ${(props) => props.theme.color.neutral[40]};
  padding-left: ${(props) => props.theme.spacing[20]};
  & > * {
    width: min-content;
  }
`;

const Row = styled.tr`
  margin-top: ${(props) => props.theme.spacing[32]};
  padding-top: ${(props) => props.theme.spacing[32]};
`;

export interface BooleanTableProps extends FieldProps<string> {
  className?: string;
  title?: string;
  columns: string[];
  rows: string[];
}

BooleanTable.displayName = 'BooleanTable';

export default BooleanTable;
